/**
 * Kontrakt kosztow modeli miedzy serwerem a klientem: typy odpowiedzi API i stale filtrow.
 *
 * Koszt to SZACUNEK z cennika (cennik/cennik.json) i zuzycia zapisanego w dzienniku wywolan
 * (LlmRequestLog), nie faktura. Kazda liczba niesie PODSTAWE, skad sie wziela, a brak danych
 * jest brakiem (null), nigdy zerem: zero jest wartoscia, a "nie wiemy" nia nie jest.
 */

/** OBLICZONY: policzony z zuzycia i stawki. BRAK_STAWKI: model spoza cennika. BRAK_DANYCH: nie ma zuzycia, z ktorego da sie liczyc. */
export type PodstawaKosztu = "OBLICZONY" | "BRAK_STAWKI" | "BRAK_DANYCH";

export const ZRODLA_KOSZTOW = ["analizy", "mikrofon"] as const;
export type ZrodloKosztow = (typeof ZRODLA_KOSZTOW)[number];

export const OKRESY_DNI = [7, 30, 90] as const;
export type OkresDni = (typeof OKRESY_DNI)[number];

/** Jeden model (dostawca plus wersja) w zestawieniu: suma jego wierszy dziennika. */
export interface WierszKosztuModelu {
  dostawca: string;
  /** Model UZYTY (wersja z odpowiedzi dostawcy, a gdy jej nie ma, zamowiona). */
  model: string;
  /** Etapy (scope), w ktorych poszly wywolania tego modelu, np. "soczewka", "brzmienie". */
  etapy: string[];
  /** Wywolania logiczne: jedno wywolanie moze miec kilka prob HTTP. */
  wywolania: number;
  /** Wiersze dziennika, czyli proby. */
  proby: number;
  /** Proby bez powodzenia (blad, timeout, porzucone). Informacja: czesc z nich mogla jednak zwrocic zuzycie i jest w sumie. */
  nieudane: number;
  /** Proby bez powodzenia I bez zapisanego zuzycia: poza kosztem, a dostawca mogl je naliczyc. */
  wylaczone: number;
  tokenyWejscia: number | null;
  tokenyWyjscia: number | null;
  /** Sekundy audio, z ktorych policzono koszt transkrypcji (tylko dostawcy rozliczajacy za czas). */
  sekundyAudio: number | null;
  /** Suma policzonych wierszy. Null, gdy zaden wiersz nie dal sie policzyc. */
  usd: number | null;
  /**
   * Prawda, gdy KAZDY wiersz, ktory powinien miec koszt, ma go W PELNI. Falsz oznacza, ze suma jest dolnym ograniczeniem:
   * brak kosztu wiersza albo wiersz policzony tylko od dolu (np. stawka audio, a dostawca nie rozbil tokenow na audio i tekst).
   */
  kompletny: boolean;
  policzone: number;
  /** Wiersze, ktorych nie dalo sie policzyc (brak stawki albo zuzycia). Wiersze policzone tylko od dolu liczy `policzone`. */
  bezKosztu: number;
  /** Podstawa najgorszego braku, a przy kompletnym wierszu OBLICZONY. */
  podstawa: PodstawaKosztu;
  /** Krotkie powody brakow, bez powtorzen, po polsku, do dymka. */
  powody: string[];
  /** Zastrzezenia z cennika (np. brak rabatu za cache), bez powtorzen. */
  uwagi: string[];
}

/** Zestawienie wg etapu (scope) wywolania. */
export interface WierszKosztuEtapu {
  etap: string;
  wywolania: number;
  proby: number;
  usd: number | null;
  kompletny: boolean;
  /**
   * Powody niekompletnosci tego etapu (brak stawki, brak danych, kwota tylko od dolu, uciety odczyt), bez powtorzen.
   * Opcjonalne: starsze odpowiedzi ich nie mialy, a klient ma wtedy ogolne zdanie.
   */
  powody?: string[];
}

export interface SumaKosztow {
  usd: number | null;
  /** Falsz: suma to dolne ograniczenie, bo czesci kosztu nie znamy. */
  kompletna: boolean;
  wywolania: number;
  proby: number;
  nieudane: number;
  wylaczone: number;
  tokenyWejscia: number | null;
  tokenyWyjscia: number | null;
}

export interface PodsumowanieKosztu {
  waluta: "USD";
  wiersze: WierszKosztuModelu[];
  wgEtapu: WierszKosztuEtapu[];
  suma: SumaKosztow;
  /** Zdania o tym, czego suma nie obejmuje albo co moze ja znieksztalcac. */
  ostrzezenia: string[];
  wersjaCennika: string;
}

/** Kurs USD/PLN z NBP (tabela A). */
export interface KursDto {
  kurs: number;
  /** Data publikacji kursu (YYYY-MM-DD). */
  data: string;
  zrodlo: string;
}

export interface KursPlnDto {
  kurs: KursDto | null;
  /** Dlaczego nie ma kursu (po polsku), gdy `kurs` to null. */
  powodBrakuKursu: string | null;
}

/** Pole `koszt` w GET /api/sessions/:id. Blad liczenia nie psuje odpowiedzi sesji. */
export type KosztSesjiDto =
  | ({ stan: "ok"; podsumowanie: PodsumowanieKosztu } & KursPlnDto)
  | { stan: "blad"; powod: string };

/** GET /api/koszty */
export interface KosztyOkresuDto extends KursPlnDto {
  okres: { dni: OkresDni; od: string; do: string };
  zrodlo: ZrodloKosztow;
  /** Ile analiz (albo prob mikrofonu) mialo wywolania w okresie. */
  liczbaAnaliz: number;
  podsumowanie: PodsumowanieKosztu;
  /** Prawda, gdy odczyt zatrzymal sie na limicie wierszy: sumy sa wtedy dolnymi ograniczeniami. */
  uciete: boolean;
}
